import type { ErroApi } from '@triade/contracts';
import { resumoIssues } from './issues';
import { getEmpresaId, getToken, notificarExpiracao } from './session';

/**
 * Cliente HTTP tipado. Dinheiro e taxa chegam como string decimal: NUNCA converta para number aqui
 * nem calcule nada no front (preco, custo, margem, imposto e arredondamento vem do motor, via API).
 */

/** Base da API: VITE_API_URL (dev/preview: '/api', com proxy do Vite). */
export const API_BASE: string = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/+$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly body: ErroApi | null;

  constructor(status: number, body: ErroApi | null) {
    // Validacao (400 com `issues`): a mensagem diz ONDE corrigir ("Corrija: Quantidade na linha 3: ..."), nao so "Validacao falhou".
    super(resumoIssues(body?.issues) || (body?.message ?? `Erro HTTP ${status}`));
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.code;
    this.body = body;
  }
}

/** Cabecalhos de autenticacao (Bearer + X-Empresa-Id) para chamadas fora de `requisitar` (ex.: stream SSE). */
export function cabecalhosAutenticados(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const empresaId = getEmpresaId();
  if (empresaId) headers['X-Empresa-Id'] = empresaId;
  return headers;
}

export type Query = Record<string, string | number | boolean | undefined | null>;

export interface RequisicaoOpcoes {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  /** false = rota publica (login) e sem header X-Empresa-Id. */
  autenticada?: boolean;
  /** true = empresa nao e exigida (ex.: GET /me/empresas). */
  semEmpresa?: boolean;
  /** Cancela a requisicao em andamento (relatorios gerados no navegador). */
  sinal?: AbortSignal;
}

export function montarUrl(path: string, query?: Query): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  }
  const s = qs.toString();
  return `${API_BASE}${path}${s ? `?${s}` : ''}`;
}

export async function requisitar<T>(path: string, opts: RequisicaoOpcoes = {}): Promise<T> {
  const autenticada = opts.autenticada ?? true;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (autenticada) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const empresaId = getEmpresaId();
    if (empresaId && !opts.semEmpresa) headers['X-Empresa-Id'] = empresaId;
  }

  const resp = await fetch(montarUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    ...(opts.sinal ? { signal: opts.sinal } : {}),
  });

  if (resp.status === 204) return undefined as T;

  const texto = await resp.text();
  const json: unknown = texto ? safeJson(texto) : null;
  if (autenticada) expirarSeSessao(resp.status, json);
  if (!resp.ok) {
    throw new ApiError(resp.status, normalizarErro(resp.status, json));
  }
  return json as T;
}

/** Baixa um arquivo gerado pela API (ex.: CSV). O servidor formata tudo; o front so entrega o arquivo. */
export async function requisitarArquivo(path: string, query?: Query, opts: { semEmpresa?: boolean } = {}): Promise<{ blob: Blob; nome: string }> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const empresaId = getEmpresaId();
  if (empresaId && !opts.semEmpresa) headers['X-Empresa-Id'] = empresaId;
  const resp = await fetch(montarUrl(path, query), { headers });
  if (!resp.ok) {
    const texto = await resp.text();
    const json = texto ? safeJson(texto) : null;
    expirarSeSessao(resp.status, json);
    throw new ApiError(resp.status, normalizarErro(resp.status, json));
  }
  const disposicao = resp.headers.get('content-disposition') ?? '';
  const nome = /filename="?([^";]+)"?/.exec(disposicao)?.[1] ?? 'arquivo';
  return { blob: await resp.blob(), nome };
}

/**
 * Envia um arquivo como multipart/form-data (campo `arquivo`). O servidor valida tipo (magic bytes) e tamanho;
 * 4xx volta como ApiError (mensagem e `code`). Sem Content-Type manual: o navegador poe o boundary.
 */
export async function enviarArquivo<T>(
  path: string,
  arquivo: Blob,
  nome: string,
  method: 'PUT' | 'POST' = 'PUT',
  opts: { semEmpresa?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const empresaId = getEmpresaId();
  if (empresaId && !opts.semEmpresa) headers['X-Empresa-Id'] = empresaId;
  const corpo = new FormData();
  corpo.append('arquivo', arquivo, nome);
  const resp = await fetch(montarUrl(path), { method, headers, body: corpo });
  const texto = await resp.text();
  const json: unknown = texto ? safeJson(texto) : null;
  expirarSeSessao(resp.status, json);
  if (!resp.ok) throw new ApiError(resp.status, normalizarErro(resp.status, json));
  return json as T;
}

/** Codigo que a API poe no 401 de SESSAO (JWT ausente/invalido/expirado, sessao invalidada). Outros 401 (ex.: token de integracao) nao deslogam. */
export const SESSAO_INVALIDA = 'SESSAO_INVALIDA';

/** So o 401 do guard de JWT encerra a sessao e volta ao login; qualquer outro 401 vira erro na tela, com o codigo. */
export function expirarSeSessao(status: number, corpo: unknown): void {
  if (status === 401 && (corpo as { code?: unknown } | null)?.code === SESSAO_INVALIDA) notificarExpiracao();
}

export function safeJson(texto: string): unknown {
  try {
    return JSON.parse(texto);
  } catch {
    return null;
  }
}

/** Garante o formato ErroApi mesmo para respostas fora do padrao (proxy, 500 sem corpo). */
export function normalizarErro(status: number, json: unknown): ErroApi {
  if (json && typeof json === 'object' && 'message' in json) {
    const o = json as Partial<ErroApi> & { message: unknown };
    const message = Array.isArray(o.message) ? o.message.join('; ') : String(o.message);
    return { ...(o as ErroApi), statusCode: o.statusCode ?? status, message };
  }
  return { statusCode: status, message: 'Erro inesperado na API' };
}

/** 422 do motor ou validacao de negocio: code/kind/details. 409: code de banco (imutabilidade, unicidade). */
export function ehErroDeNegocio(err: unknown): err is ApiError {
  return err instanceof ApiError && (err.status === 422 || err.status === 409);
}
